import { Fragment, type ReactNode } from 'react';
import { cn } from './cn';
import { Spinner } from './Button';
import { EmptyState } from './EmptyState';

function ItemMeta({ avatar, title, description }: { avatar?: ReactNode; title?: ReactNode; description?: ReactNode }) {
  return (
    <div className="flex min-w-0 items-start gap-3">
      {avatar != null && <div className="shrink-0">{avatar}</div>}
      <div className="min-w-0">
        {title != null && <div className="truncate font-medium text-content">{title}</div>}
        {description != null && <div className="mt-0.5 text-sm text-muted">{description}</div>}
      </div>
    </div>
  );
}

function Item({ children, actions, className, extra, onClick, style }: {
  children?: ReactNode; actions?: ReactNode[]; className?: string; extra?: ReactNode; onClick?: () => void; style?: import('react').CSSProperties;
}) {
  return (
    <li onClick={onClick} style={style} className={cn('flex items-center justify-between gap-3 px-1 py-3', onClick && 'cursor-pointer', className)}>
      <div className="min-w-0 flex-1">{children}</div>
      {extra != null && <div className="shrink-0">{extra}</div>}
      {actions && actions.length > 0 && <div className="flex shrink-0 items-center gap-3">{actions.map((a, i) => <span key={i}>{a}</span>)}</div>}
    </li>
  );
}

const ListItem = Object.assign(Item, { Meta: ItemMeta });

interface ListProps<T> {
  dataSource?: T[];
  renderItem?: (item: T, index: number) => ReactNode;
  loading?: boolean;
  header?: ReactNode;
  footer?: ReactNode;
  bordered?: boolean;
  split?: boolean;
  size?: 'small' | 'default' | 'large';
  className?: string;
  locale?: { emptyText?: ReactNode };
  children?: ReactNode;
}

/** Ant Design `List` replacement (+ `List.Item` / `List.Item.Meta`). */
function ListBase<T>({ dataSource, renderItem, loading, header, footer, bordered, split = true, className, locale, children }: ListProps<T>) {
  const items = dataSource ?? [];
  return (
    <div className={cn(bordered && 'overflow-hidden rounded-card border border-line bg-surface', className)}>
      {header != null && <div className="border-b border-line px-4 py-2.5 font-semibold text-content">{header}</div>}
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div>
      ) : renderItem && items.length === 0 ? (
        <EmptyState compact title={locale?.emptyText ?? 'No data'} />
      ) : (
        <ul className={cn('px-4', split && '[&>li]:border-b [&>li]:border-line/70 [&>li:last-child]:border-0')}>
          {/* Keyed here, not by the caller: `renderItem` hands back a <List.Item>, and asking every
              call site to remember a key on it is how the React "unique key" warning got into the
              console on the Roles page. Prefer the row's own id so reordering keeps identity. */}
          {renderItem
            ? items.map((it, i) => (
              <Fragment key={String((it as any)?.id ?? (it as any)?.key ?? i)}>{renderItem(it, i)}</Fragment>
            ))
            : children}
        </ul>
      )}
      {footer != null && <div className="border-t border-line px-4 py-2.5 text-muted">{footer}</div>}
    </div>
  );
}

export const List = Object.assign(ListBase, { Item: ListItem });
